/* eslint-disable max-lines-per-function, max-statements */
/* global Element, IntersectionObserver, MutationObserver, getComputedStyle */
/* global requestAnimationFrame, cancelAnimationFrame */
import { RefObject, useEffect } from 'react';

import { recordActionBarObservation } from '../../services/actionBarAnalyticsService';

import { createActionInvocationId } from './useActionBarClicks';

const BUTTONS = 'button.ai-overview-action-btn, button.agent-suggestion-pill, button[role="menuitem"]';
const MENU = '[data-action-analytics-owner]';

export const actionAnalyticsLabel = (button: HTMLButtonElement): string => (
  button.querySelector('.ai-overview-action-btn__label')?.textContent
  || button.getAttribute('aria-label') || button.textContent || 'Action'
).trim().replace(/\s+/g, ' ').slice(0, 160);

// Labels distinguish Continue's concrete actions (e.g. Generate First Page).
// A small stable hash also handles non-Latin labels without storing page content.
export const actionAnalyticsKey = (label: string): string => {
  const normalized = label.toLowerCase();
  let hash = 2166136261;

  for (const char of normalized) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);

  return `${normalized.replace(/[^a-z0-9]+/g, '_').slice(0, 80) || 'action'}_${(hash >>> 0).toString(16)}`;
};

export const isActionVisible = (button: HTMLButtonElement): boolean => {
  if (!button.isConnected || document.visibilityState === 'hidden'
    || button.closest('[hidden], [aria-hidden="true"], [data-action-overflow-hidden="true"]')) return false;
  const style = getComputedStyle(button);
  const rect = button.getBoundingClientRect();

  return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > 0
    && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
    && rect.top < window.innerHeight && rect.left < window.innerWidth;
};

/** One observer for the visible dock and its menus; hidden message sources are excluded. */
export function useActionBarAnalytics(
  rootRef: RefObject<HTMLElement | null>, projectId?: string | null, scope?: string,
): void {
  useEffect(() => {
    const root = rootRef.current;

    if (!projectId || !scope || !root) return undefined;
    let frame: number | undefined;
    const exposures = new Map<string, { id: string; label: string; key: string }>();
    const observed = new Set<HTMLButtonElement>();
    const intersecting = new Map<HTMLButtonElement, boolean>();
    const ownedMenu = (element: Element): boolean => element.getAttribute('data-action-analytics-owner') === scope;
    const menus = (): Element[] => Array.from(document.querySelectorAll(MENU)).filter(ownedMenu);
    const belongs = (button: HTMLButtonElement): boolean => root.contains(button)
      || Boolean(button.closest(MENU) && ownedMenu(button.closest(MENU)!));
    const identity = (button: HTMLButtonElement): string => {
      const owner = root.getAttribute('data-source-entry-id')
        || root.querySelector('[data-source-entry-id]')?.getAttribute('data-source-entry-id') || 'project';

      return `${owner}:${button.closest(MENU) ? 'menu' : 'bar'}:${actionAnalyticsKey(actionAnalyticsLabel(button))}`;
    };
    const expose = (button: HTMLButtonElement) => {
      const key = identity(button);
      let exposure = exposures.get(key);

      if (!exposure) {
        const label = actionAnalyticsLabel(button);

        exposure = { id: createActionInvocationId(), label, key: actionAnalyticsKey(label) };
        exposures.set(key, exposure);
        recordActionBarObservation(projectId, { event_id: exposure.id, impression_id: exposure.id,
          kind: 'impression', button_key: exposure.key, label });
      }

      return exposure;
    };
    const scan = () => {
      frame = undefined;
      const buttons = [root, ...menus()].flatMap((element) => (
        Array.from(element.querySelectorAll<HTMLButtonElement>(BUTTONS))
      ));
      const visible = new Set<string>();

      buttons.forEach((button) => {
        if (!observed.has(button)) { observer?.observe(button); observed.add(button); }
        if (isActionVisible(button)) {
          // Keep an existing exposure while a replacement DOM node awaits its
          // first intersection result; a remount is not a new visible action.
          if (!observer || intersecting.get(button) !== false) visible.add(identity(button));
          if (!observer || intersecting.get(button)) expose(button);
        }
      });
      exposures.forEach((_value, key) => { if (!visible.has(key)) exposures.delete(key); });
      observed.forEach((button) => {
        if (!buttons.includes(button)) {
          observer?.unobserve(button); observed.delete(button); intersecting.delete(button);
        }
      });
    };
    const schedule = () => { if (frame === undefined) frame = requestAnimationFrame(scan); };
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver((entries) => {
      entries.forEach((entry) => intersecting.set(entry.target as HTMLButtonElement, entry.isIntersecting));
      schedule();
    });
    const changes = new MutationObserver(schedule);
    const observeChanges = () => {
      changes.disconnect();
      [root, ...menus()].forEach((element) => changes.observe(element, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'data-action-overflow-hidden', 'data-source-entry-id'],
      }));
    };
    const bodyChanges = new MutationObserver((records) => {
      if (!records.some((record) => [...Array.from(record.addedNodes), ...Array.from(record.removedNodes)]
        .some((node) => node instanceof Element && node.matches(MENU)))) return;
      observeChanges();
      schedule();
    });
    const click = (event: MouseEvent) => {
      const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>(BUTTONS) : null;

      // Mirroring calls hidden source .click(); only the original visible user event counts.
      if (!event.isTrusted || !button || !belongs(button) || button.disabled || !isActionVisible(button)) return;
      const exposure = expose(button);

      recordActionBarObservation(projectId, { event_id: createActionInvocationId(), impression_id: exposure.id,
        kind: 'click', button_key: exposure.key, label: exposure.label });
    };

    observeChanges();
    bodyChanges.observe(document.body, { childList: true });
    document.addEventListener('click', click, true);
    document.addEventListener('visibilitychange', schedule);
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
      observer?.disconnect(); changes.disconnect(); bodyChanges.disconnect();
      document.removeEventListener('click', click, true);
      document.removeEventListener('visibilitychange', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [projectId, scope, rootRef]);
}
